import { reportFailure } from "./telemetry";
import { captureError } from "./product-analytics";
import { ReportScreenshots } from "./ReportScreenshots";
import type { ReportScreenshot } from "../shared/report-screenshots";
import {
  Component,
  useEffect,
  useRef,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { Bug, Check, Download, LockKeyhole, Send, X } from "lucide-react";
import { captureDiagnostics, recordDiagnostic } from "./diagnostics";
import type { BugReport } from "../shared/bug-reports";
import { sendBugReport } from "./bug-report-client";
import { useReportSession } from "./useReportSession";
import "./bug-reporter.css";

export class WorkspaceErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    reportFailure("react", "other", error);
    captureError(error, {
      kind: "react",
      componentStack: info.componentStack,
    });
    recordDiagnostic(
      "react",
      `${error.message}\n${error.stack ?? ""}\n${info.componentStack ?? ""}`,
    );
  }
  render() {
    return this.state.failed ? (
      <div className="loading">
        <h1>The workspace hit a problem.</h1>
        <p>Use Report a bug before reloading to include the error details.</p>
        <button onClick={() => location.reload()}>Reload workspace</button>
      </div>
    ) : (
      this.props.children
    );
  }
}

export function BugReporter() {
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const done = useRef<HTMLButtonElement>(null);
  const submitting = useRef(false);
  const [opened, setOpened] = useState(false);
  const [snapshot, setSnapshot] = useState<BugReport["diagnostics"]>(null);
  const [message, setMessage] = useState("");
  const [include, setInclude] = useState(true);
  const [id, setId] = useState(() => crypto.randomUUID());
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState("");
  const [error, setError] = useState("");
  const [downloaded, setDownloaded] = useState(false);
  const [screenshots, setScreenshots] = useState<ReportScreenshot[]>([]);
  const [imageBusy, setImageBusy] = useState(false);
  const [imagePending, setImagePending] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const { session, refresh } = useReportSession(opened && !sent);
  // Keep the reviewed payload and reference across closing, offline and retries.
  const report: BugReport = {
    id,
    message: message.trim(),
    diagnostics: include ? snapshot : null,
    screenshots,
  };
  const blocked = sending || imageBusy || imagePending || !message.trim();
  function edited() {
    setId(crypto.randomUUID());
    setError("");
    setDownloaded(false);
  }
  function open() {
    if (dialog.current?.open || capturing) return;
    if (sent) {
      setMessage("");
      setScreenshots([]);
      setSent("");
      edited();
    }
    if (!snapshot || sent) setSnapshot(captureDiagnostics());
    setOpened(true);
    dialog.current?.showModal();
    dialog.current?.querySelector<HTMLTextAreaElement>("textarea")?.focus();
  }
  function close() {
    if (!sending && !imageBusy) dialog.current?.close();
  }
  useEffect(() => {
    const onOpen = () => trigger.current?.click();
    window.addEventListener("planroom:report-bug", onOpen);
    return () => window.removeEventListener("planroom:report-bug", onOpen);
  }, []);
  useEffect(() => {
    if (sent) done.current?.focus();
  }, [sent]);
  function download() {
    if (blocked) return;
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(report, null, 2)], { type: "application/json" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `calque-bug-${id}.json`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setDownloaded(true);
  }
  async function submit() {
    if (submitting.current || blocked || session !== "signed-in") return;
    submitting.current = true;
    setSending(true);
    setError("");
    try {
      await sendBugReport(report);
      setSent(id);
    } catch (e) {
      setError(
        (e as Error).name === "TimeoutError"
          ? "Sending timed out. Retry or download your report."
          : (e as Error).message,
      );
    } finally {
      submitting.current = false;
      setSending(false);
    }
  }
  return (
    <>
      <button
        ref={trigger}
        className="bug-trigger"
        hidden={capturing}
        onClick={open}
      >
        <Bug size={15} /> Report a bug
      </button>
      <dialog
        className="bug-dialog"
        ref={dialog}
        aria-labelledby="bug-title"
        onClose={() => {
          // A cancelled screen picker can reopen before the queued close event.
          if (dialog.current?.open) return;
          setOpened(false);
          if (!capturing) trigger.current?.focus();
        }}
        onCancel={(e) => {
          e.preventDefault();
          close();
        }}
        onKeyDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <header className="bug-header">
          <div
            className={`bug-heading-icon${sent ? " is-sent" : ""}`}
            aria-hidden="true"
          >
            {sent ? <Check size={21} /> : <Bug size={21} />}
          </div>
          <div>
            <h2 id="bug-title">{sent ? "Report received" : "Report a bug"}</h2>
          </div>
          <button
            className="bug-close"
            aria-label="Close bug report"
            disabled={sending || imageBusy}
            onClick={close}
          >
            <X size={20} />
          </button>
        </header>
        {sent ? (
          <div className="bug-success">
            <p role="status">Saved privately for Rohan.</p>
            <details className="bug-reference">
              <summary>Report reference</summary>
              <code>{sent}</code>
            </details>
            <button ref={done} className="primary" onClick={close}>
              Done
            </button>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <div className="bug-body">
              <div className="bug-message-field">
                <div className="bug-field-heading">
                  <label htmlFor="bug-message">What went wrong?</label>
                  {message.length > 4500 && (
                    <span className="bug-character-count">
                      {message.length.toLocaleString()} / 5,000
                    </span>
                  )}
                </div>
                <textarea
                  id="bug-message"
                  autoFocus
                  required
                  maxLength={5000}
                  rows={4}
                  disabled={sending}
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    edited();
                  }}
                  placeholder="Describe the problem…"
                />
              </div>
              <ReportScreenshots
                images={screenshots}
                disabled={sending}
                onBusy={setImageBusy}
                onPending={setImagePending}
                onChange={(images) => {
                  setScreenshots(images);
                  edited();
                }}
                onCaptureVisibility={(hidden) => {
                  setCapturing(hidden);
                  if (hidden) dialog.current?.close();
                  else {
                    setOpened(true);
                    dialog.current?.showModal();
                  }
                }}
              />
              <section
                className="bug-diagnostics"
                aria-label="Technical details"
              >
                <label className="bug-checkbox">
                  <input
                    type="checkbox"
                    checked={include}
                    disabled={sending}
                    onChange={(e) => {
                      setInclude(e.target.checked);
                      edited();
                    }}
                  />
                  <span>Include error details</span>
                </label>
                <details className="bug-capture-help">
                  <summary>Review</summary>
                  <p className="bug-help">
                    Recent errors and connection events. No cookies or canvas
                    contents. Review for private text before sending.
                  </p>
                  <pre tabIndex={0} aria-label="Technical details preview">
                    {JSON.stringify(snapshot, null, 2)}
                  </pre>
                </details>
              </section>
              {session === "checking" && (
                <p className="bug-help" role="status">
                  Checking sign-in…
                </p>
              )}
              {session === "signed-out" && (
                <p className="bug-notice">
                  Sign in to send, or download your report.
                </p>
              )}
              {session === "unavailable" && (
                <div className="bug-notice" role="status">
                  Can’t connect. Download the report or try again.{" "}
                  <button
                    type="button"
                    className="bug-text-button"
                    onClick={refresh}
                  >
                    Retry
                  </button>
                </div>
              )}
              {error && (
                <p role="alert" className="bug-error">
                  {error}
                </p>
              )}
              {downloaded && (
                <p className="bug-notice" role="status">
                  Downloaded, not sent. Share the file with Rohan.
                </p>
              )}
            </div>
            <footer className="bug-footer">
              <p
                className="bug-privacy"
                title="Shared with Rohan only when you send."
              >
                <LockKeyhole size={13} aria-hidden="true" /> Private report
              </p>
              <div className="bug-actions">
                <button
                  type="button"
                  className="bug-download"
                  aria-label="Download report"
                  title="Download report"
                  disabled={blocked}
                  onClick={download}
                >
                  <Download size={16} /> Download
                </button>
                <button
                  className="primary"
                  disabled={blocked || session !== "signed-in"}
                >
                  <Send size={15} /> {sending ? "Sending…" : "Send report"}
                </button>
              </div>
            </footer>
          </form>
        )}
      </dialog>
    </>
  );
}
